import { ParsedDeliveryItem } from './lufaDeliveryParse';
import { FoodCategory, ShelfLifeBucket, toShelfLifeBucket } from '../types/foodRecognition';
import { hasKeyword } from './pantryLocation';
// Type-only: the email-import handler imports this module server-side, and a
// value import would drag a React component into the API bundle.
import type { EditableDetectedItem } from '../types/foodRecognition';

const CATEGORY_MAP: Record<string, FoodCategory> = {
  produce: 'Vegetables',
  meat: 'Meat',
  'plant-protein': 'Plant-Based Protein',
  dairy: 'Dairy',
  bakery: 'Bread/Grains',
  frozen: 'Packaged Foods',
  other: 'Other',
};

// Legacy 'protein' responses lumped animals and legumes together and sent both
// to Plant-Based Protein — which is why every chicken, ham and fillet in the
// pantry wore the tofu icon. The prompt now distinguishes them; these keywords
// cover responses issued before it did.
const MEAT_KEYWORDS = [
  'chicken', 'poulet', 'beef', 'boeuf', 'pork', 'porc', 'lamb', 'agneau', 'veal', 'veau',
  'turkey', 'dinde', 'duck', 'canard', 'bacon', 'ham', 'jambon', 'sausage', 'saucisse',
  'salami', 'prosciutto', 'charcuterie', 'fish', 'poisson', 'salmon', 'saumon', 'trout',
  'truite', 'walleye', 'dore', 'cod', 'morue', 'tuna', 'thon', 'shrimp', 'crevette',
  'scallop', 'petoncle', 'mussel', 'moule', 'crab', 'crabe', 'lobster', 'homard', 'meat', 'viande',
];

/** Lufa's coarse category -> the pantry's food category. */
export function deliveryCategoryToPantryCategory(category: string, name = ''): FoodCategory {
  if (category === 'protein') {
    return MEAT_KEYWORDS.some((k) => hasKeyword(name, k)) ? 'Meat' : 'Plant-Based Protein';
  }
  return CATEGORY_MAP[category] || 'Other';
}

/**
 * The shelf-life bucket for a delivery line, when the model did not supply one.
 *
 * This used to be the *only* answer, and it was blind to the product name: an
 * entire order's proteins got one number and its produce got another. It is now
 * a last resort behind the model's own reading and the shelf-life table, and it
 * exists only so an old-format response still lands somewhere sane.
 */
export function shelfCategoryForDeliveryItem(
  item: Pick<ParsedDeliveryItem, 'category' | 'isFrozen' | 'shelfLife'>
): ShelfLifeBucket {
  // Validated, not trusted: the model picks this string, and one it invents
  // would otherwise become a pantry row with no date that reads as fresh forever.
  const fromModel = toShelfLifeBucket(item.shelfLife);
  if (fromModel) return fromModel;
  if (item.isFrozen || item.category === 'frozen') return 'shelf-stable';
  if (item.category === 'produce') return 'perishable';
  if (item.category === 'meat') return 'highly-perishable';
  return 'week-plus';
}

export interface DeliveryInventoryItem extends EditableDetectedItem {
  source: 'lufa-delivery';
  deliveryDate: string | null;
}

export function convertDeliveryItemsToInventory(
  items: ParsedDeliveryItem[],
  deliveryDate: string | null
): DeliveryInventoryItem[] {
  return items.map((item) => ({
    id: `lufa_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name: item.name,
    ...(item.nameFr ? { names: { en: item.name, fr: item.nameFr } } : {}),
    category: deliveryCategoryToPantryCategory(item.category, item.name),
    confidence: 90,
    quantity_estimate: item.quantity || 1,
    quantity: item.quantity || 1,
    unit: item.unit,
    visible_condition: 'fresh' as const,
    expiry_category: shelfCategoryForDeliveryItem(item),
    isAccepted: true,
    isEditing: false,
    source: 'lufa-delivery' as const,
    deliveryDate,
  }));
}
